import React, { useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFileImage, 
  faCloudUploadAlt, 
  faTimes,
  faDownload,
  faRedo
} from "@fortawesome/free-solid-svg-icons";

export default function PngToJpgConverter() {
  const { darkMode } = useTheme();
  const [imageSrc, setImageSrc] = useState(null);
  const [fileName, setFileName] = useState('');
  const [jpgUrl, setJpgUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadAreaRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    
    setFileName(file.name);
    
    if (file.type === "image/png") {
      setIsProcessing(true);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageSrc(event.target.result);
        convertToJpg(event.target.result);
        setIsProcessing(false);
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please upload a valid PNG file.");
      clearSelectedFile();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const convertToJpg = (src) => {
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; // For transparent background
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const jpg = canvas.toDataURL("image/jpeg", 1.0); // High quality
      setJpgUrl(jpg);
    };
    img.src = src;
  };

  const clearSelectedFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFileName('');
    setImageSrc(null);
    setJpgUrl(null);
  };

  const resetForm = () => {
    clearSelectedFile();
  };

  return (
    <>
      <HeaderSection toolId="pngToJpg" />
      <div className={`max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`bg-white dark:bg-gray-800 rounded-xl dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 ${
          darkMode 
            ? 'bg-gray-800 border-gray-700 shadow-gray-900/10' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Header Section */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FontAwesomeIcon icon={faFileImage} className="mr-3" />
              PNG to JPG Converter
            </h1>
            <p className="opacity-90">
              Convert PNG images to JPG format with high quality
            </p>
          </div>

          {/* Upload Area */}
          <section 
            ref={uploadAreaRef}
            className={`border-2 border-dashed rounded-xl py-10 px-4 md:px-20 m-6 text-center cursor-pointer transition-all duration-300 ${
              isDragging 
                ? 'border-green-500 bg-green-100 dark:bg-green-900/30' 
                : fileName 
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-300 hover:border-green-400 hover:bg-green-50 dark:hover:bg-gray-700'
            }`}
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FontAwesomeIcon 
              icon={faCloudUploadAlt} 
              className={`text-4xl md:text-5xl mb-4 p-6 transition-transform duration-300 hover:scale-110 ${
                darkMode ? 'text-green-400' : 'text-green-500'
              }`} 
            />
            <p className={darkMode ? 'text-gray-200' : 'text-gray-700'}>
              Drag & drop your PNG file here or click to browse
            </p>
            <input 
              type="file" 
              ref={fileInputRef}
              className="hidden" 
              accept="image/png"
              onChange={handleInputChange}
            />
            {fileName && (
              <div className="mt-4 text-sm font-medium flex items-center justify-center gap-2">
                <span className={darkMode ? 'text-gray-200' : 'text-green-600'}>{fileName}</span>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSelectedFile();
                  }}
                  className={`transition-colors duration-300 ${
                    darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'
                  }`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
          </section>

          {/* Preview Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-6">
            {imageSrc && (
              <div className={`border rounded-lg p-4 transition-colors duration-300 ${
                darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
              }`}>
                <h3 className={`text-sm font-medium flex items-center gap-2 mb-3 ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`}>
                  <FontAwesomeIcon icon={faFileImage} />
                  Original PNG Preview
                </h3>
                <div className="flex justify-center">
                  <img
                    src={imageSrc}
                    alt="PNG Preview"
                    className="max-w-full h-auto rounded-md shadow"
                  />
                </div>
              </div>
            )}

            {jpgUrl && (
              <div className={`border rounded-lg p-4 transition-colors duration-300 ${
                darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
              }`}>
                <h3 className={`text-sm font-medium flex items-center gap-2 mb-3 ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`}>
                  <FontAwesomeIcon icon={faFileImage} />
                  Converted JPG Preview
                </h3>
                <div className="flex justify-center">
                  <img
                    src={jpgUrl}
                    alt="JPG Preview"
                    className="max-w-full h-auto rounded-md shadow"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <footer className="flex flex-col sm:flex-row gap-3 m-6">
            <a
              href={jpgUrl || '#'}
              download={fileName ? fileName.replace('.png', '.jpg') : 'converted.jpg'}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                jpgUrl 
                  ? darkMode
                    ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                    : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'
                  : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-70 text-gray-600 dark:text-gray-300'
              }`}
              onClick={(e) => !jpgUrl && e.preventDefault()}
            >
              {isProcessing ? (
                <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} />
                  Download JPG
                </>
              )}
            </a>
            <button
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                darkMode
                  ? 'border border-green-400 text-green-400 hover:bg-gray-700'
                  : 'border border-green-500 text-green-600 hover:bg-green-50'
              }`}
              onClick={resetForm}
            >
              <FontAwesomeIcon icon={faRedo} />
              Reset
            </button>
          </footer>
        </div>
      </div>
      <canvas ref={canvasRef} className="hidden" />

      <BottomSection toolId="pngToJpg" />
    </>
  );
}